'use client';

import { Video } from '@/react/components/core-ui/Video';
import { MediaPlayer } from '@/react/components/core-ui/Video/MediaPlayer';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import MediaService from '@/services/isomorphic/MediaService';
import type { IImage } from '@/services/models/Media/Image';
import type { IVideo } from '@/services/models/Media/Video';
import { useMemo, type FC } from 'react';
import LoggerService from '@/services/isomorphic/LoggerService';
import type { ICloudinaryMedia } from '../../template-data';
import type { IContentMediaItem } from '../../template-data/IContentMedia';
import { getAspectRatioNumberFromEnum } from '../../utilities/aspectRatioUtils';
import { getMediaWidthFromElementWidth } from '../../utilities/getMediaWidthFromElementWidth';
import { PlayerOverlay } from './PlayerOverlay';

import S from './styles.base.module.scss';

/**
 * The poster options are used to optimize the poster image for the video.
 */
export interface IPosterOptions {
  /**
   * The width of the poster.
   */
  width: number;
  /**
   * The aspect ratio of the poster.
   */
  aspectRatio: number;
  /**
   *  The quality of the poster.
   */
  quality: number;
}

/**
 * Gets the optimized poser url when given a cloudinary media item from
 * the cloudinary plugin of builder.io.
 * @param poster - The poster media item.
 * @param options - The options for the poster.
 * @returns The optimized poster url or undefined.
 */
const getPoster = (
  poster: ICloudinaryMedia,
  options: IPosterOptions
): string => {
  const altText = poster.context?.custom.alt ?? '';
  if (!altText) {
    LoggerService.warn('ContentVideo: Poster image is missing alt text.');
  }

  const { width, aspectRatio, quality } = options;

  let optimizedPoster = poster?.url ?? undefined;

  const posterSrc = new URL(optimizedPoster);
  posterSrc.protocol = 'https:';

  const posterImage: IImage = {
    uuid: poster.public_id ?? '',
    src: posterSrc.toString(),
    alt: poster.context?.custom.alt ?? '',
    aspectRatio
  };

  optimizedPoster = MediaService.optimizeMedia(posterImage, {
    width,
    aspectRatio,
    quality,
    fit: 'auto',
    gravity: 'auto',
    omitNamedTransformation: true
  }).src;

  return optimizedPoster;
};

export interface IContentVideoProps {
  /**
   * The array of links to be turned into buttons.
   */
  media: IContentMediaItem;
  /**
   * The current width of the image, this has been assessed in the ContentMedia component.
   */
  elementWidth: number;
}

/**
 * A component to render a video.
 */
export const ContentVideo: FC<IContentVideoProps> = ({
  media,
  elementWidth
}) => {
  const { item, videoSettings, featured, aspectRatio: aspectRatioEnum } = media;
  const objectFit = videoSettings.objectFit ?? 'cover';
  const aspectRatio = getAspectRatioNumberFromEnum(aspectRatioEnum) ?? 1;

  const quality =
    ConfigurationService.getConfig('content').getSetting('videoQuality').value;

  const showPlaybackProgress = ConfigurationService.getConfig('ui').getSetting(
    'video.showPlaybackProgress'
  ).value;

  // If showPlaybackProgress is enabled we are using custom controls, so then we don't want the video to
  // have controls as well. Otherwise we use the control setting on the video itself.
  const controlsOverride = !showPlaybackProgress && videoSettings.controls;

  const width = getMediaWidthFromElementWidth(
    featured ? elementWidth * 1.5 : elementWidth
  );
  const posterWidth = getMediaWidthFromElementWidth(elementWidth);

  // Stub for image transformation
  const video = useMemo((): IVideo => {
    const src = new URL(item!.url);
    // All cloudinary images are coming from the builder.io plugin with http protocol.
    src.protocol = 'https:';

    const { autoplay, loop, muted, poster } = videoSettings;

    // If the video intends to autoplay, skip optimizing the poster
    // because it will be onscreen for a very short time anyways.
    // This prevents the poster from flashing on slower networks.
    const optimizedPoster = autoplay
      ? poster?.url
      : poster
        ? getPoster(poster, { width: posterWidth, aspectRatio, quality })
        : undefined;

    const video: IVideo = {
      uuid: item!.public_id,
      src: src.toString(),
      poster: optimizedPoster,
      settings: { autoplay, loop, controls: controlsOverride, muted }
    };

    const optimizedVideo = MediaService.optimizeMedia(video, {
      width,
      aspectRatio,
      quality,
      fit: 'fill',
      omitNamedTransformation: true
    });

    return optimizedVideo;
  }, [
    aspectRatio,
    controlsOverride,
    item,
    posterWidth,
    quality,
    videoSettings,
    width
  ]);

  return (
    <div className={S.container} style={{ aspectRatio }}>
      <MediaPlayer key={video.src} settings={video.settings}>
        <Video video={video} fit={objectFit} />
        {showPlaybackProgress && <PlayerOverlay />}
      </MediaPlayer>
    </div>
  );
};

export default ContentVideo;
